import { useTheme } from '@/contexts/ThemeContext';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { LuxModalBody } from './LuxModalBody';
import { LuxModalFooter } from './LuxModalFooter';
import { LuxModalHeader, type LuxModalVariant } from './LuxModalHeader';

const LUX_MODAL_Z_INDEX = 50;

export type LuxModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

const sizeClasses: Record<LuxModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-4xl',
  full: 'max-w-[95vw]',
};

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface LuxModalProps {
  /** La modale est-elle ouverte */
  isOpen: boolean;
  /** Callback de fermeture */
  onClose: () => void;
  /** État de l'animation (in, out, null) - si non fourni, géré en interne */
  animationState?: 'in' | 'out' | null;
  /** Contenu de la modale */
  children: ReactNode;
  /** Taille de la modale */
  size?: LuxModalSize;
  /** Variante du header (si LuxModalHeader utilisé) */
  variant?: LuxModalVariant;
  /** Fermer au clic sur le backdrop */
  closeOnBackdrop?: boolean;
  /** Fermer à la touche Escape */
  closeOnEscape?: boolean;
  /** Afficher le bouton X dans le header (si LuxModalHeader utilisé) */
  showCloseButton?: boolean;
  /** Classes CSS additionnelles pour le conteneur */
  className?: string;
}

export function LuxModal({
  isOpen,
  onClose,
  animationState: controlledAnimationState,
  children,
  size = 'md',
  variant: _variant = 'default',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton: _showCloseButton = true,
  className = '',
}: LuxModalProps) {
  const { resolvedTheme } = useTheme();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const animationState = controlledAnimationState ?? (isOpen ? 'in' : null);

  const bgClass =
    resolvedTheme === 'dark' ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200';

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnBackdrop && e.target === e.currentTarget) {
        onClose();
      }
    },
    [closeOnBackdrop, onClose]
  );

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement | null;
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleEscape]);

  useEffect(() => {
    if (!isOpen) return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    firstFocusable?.focus();

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      previousActiveElement.current?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const animationClass =
    animationState === 'in'
      ? 'lux-modal-zoom-in'
      : animationState === 'out'
        ? 'lux-modal-zoom-out'
        : '';

  return (
    <div
      className="fixed inset-0 overflow-y-auto flex items-center justify-center min-h-screen p-4 sm:p-6"
      style={{ zIndex: LUX_MODAL_Z_INDEX }}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity ${
          animationState === 'in'
            ? 'backdrop-fade-in'
            : animationState === 'out'
              ? 'backdrop-fade-out'
              : ''
        }`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal content */}
      <div
        ref={modalRef}
        className={`
          relative w-full ${sizeClasses[size]} mx-auto
          rounded-token-lg shadow-2xl border
          transform ${animationClass}
          ${bgClass}
          ${className}
        `}
        onClick={handleContentClick}
      >
        {children}
      </div>
    </div>
  );
}

LuxModal.Header = LuxModalHeader;
LuxModal.Body = LuxModalBody;
LuxModal.Footer = LuxModalFooter;
